import { useState, useCallback, useEffect, useRef } from 'react';
import { useProjectStore } from '../../../store/useProjectStore';
import { useConfigStore } from '../../../store/useConfigStore';
import { useDebugStore } from '../../../store/useDebugStore';
import { useJobManager } from '../../../contexts/JobManagerContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getLineSegments } from '../../../utils/textUtils';
import { synonymGroupsToFlatText, findMatchingSynonymGroupsForSegments } from '../../../utils/glossaryUtils';
import { TranslationFile } from '../../../types';
import { supabase } from '../../../services/supabase/supabaseClient';

/**
 * Paragraph-level source segments for training UI & AI prompts.
 * Training doesn't need format preservation → merge layer 2 run-groups back into paragraphs.
 * Falls back to getLineSegments for files without backendParagraphs (legacy).
 */
export function getTrainSourceSegments(file: TranslationFile): string[] {
    if (file.backendParagraphs && file.backendSegments) {
        const bSegments = file.backendSegments;
        return file.backendParagraphs
            .map(p => p.groups.map(g =>
                g.aiIdx !== undefined ? (bSegments[g.aiIdx] ?? '') : (g.wsText ?? '')
            ).join(''))
            .filter(t => t.trim());
    }
    return getLineSegments(file.sourceText).map(s => s.text);
}

// ─── Train chain subscription state ────────────────────────────────────────
// Lưu audit_id của chain đang chạy để subscription theo dõi đúng chain.
// Nếu user click "Bắt đầu" lần nữa, auditJobId mới sẽ thay thế subscription cũ.
interface ActiveChain {
    fileId: string;
    auditId: string;
    translateRunId: string;
    totalBatches: number;
    translatedBatches: number;   // đếm translate batch done
    matchDone: boolean;
}

/**
 * useTrainWorkflow Hook — REFACTORED Phase 4.3
 *
 * Thay đổi chính: bỏ sequential client orchestration (addJob → runJobAndWait × 3).
 * Thay bằng 1 RPC call `create_train_chain` → server tự lo toàn bộ chain M→T→A.
 * Client chỉ subscribe jobs table theo `update_to = audit_id` để cập nhật UI.
 *
 * Luồng mới:
 *  1. runFileAuto() → build config, chunk segments, call `create_train_chain` RPC
 *  2. Server INSERT 1 match + N translate + 1 audit (atomic)
 *  3. Client subscribe jobs WHERE update_to=audit_id → cập nhật matchStatus / translationStatus
 *  4. Subscribe pending_sync OR audit job WHERE id=audit_id → cập nhật auditStatus + result
 *  5. useBatchRecovery (existing, không đổi) tự merge pending_sync rows khi mở lại app
 *
 * Backward compat:
 *  - runAuditStep() giữ nguyên (dùng khi chạy audit riêng sau khi đã có translate result)
 *  - runAllTrain() gọi runFileAuto() như trước
 *  - Return shape không đổi → components không vỡ
 */
export const useTrainWorkflow = () => {
    const { files, projects, updateFile } = useProjectStore();
    const { config } = useConfigStore();
    const { addDebugLog } = useDebugStore();
    const { addJob, completeJob, failJob } = useJobManager();
    const { user } = useAuth();

    const [isTranslating, setIsTranslating] = useState(false);
    const [isMatching, setIsMatching] = useState(false);
    const [isAuditing, setIsAuditing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [auditStreamingText, setAuditStreamingText] = useState('');
    const [auditThinkingText, setAuditThinkingText] = useState('');

    // Ref lưu active chain để subscription có thể đọc latest state không bị stale closure
    const activeChainRef = useRef<ActiveChain | null>(null);

    // ─── Subscription: theo dõi jobs của chain đang chạy ──────────────────
    // Subscribe jobs table filter `update_to=eq.<auditId>` để cập nhật UI:
    //  - semantic-match done → matchStatus='done'
    //  - translate batch done → đếm translatedBatches, khi đủ → translationStatus='done'
    //  - Nếu match/translate error → cập nhật error trên file
    // Subscribe audit job (WHERE id=auditId) để bắt audit done/error.
    // Cleanup: unsubscribe khi auditId đổi hoặc component unmount.

    const [auditSubscribeId, setAuditSubscribeId] = useState<string | null>(null);

    useEffect(() => {
        if (!auditSubscribeId) return;

        const chain = activeChainRef.current;
        if (!chain || chain.auditId !== auditSubscribeId) return;

        const { auditId, fileId } = chain;

        // Channel 1: lắng nghe các upstream jobs (match + translate batches) qua update_to=auditId
        const upstreamChannel = supabase
            .channel(`train-upstream-${auditId}`)
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'public',
                table: 'jobs',
                filter: `update_to=eq.${auditId}`,
            }, (payload) => {
                const job = payload.new as any;
                const currentChain = activeChainRef.current;
                if (!currentChain || currentChain.auditId !== auditId) return;

                if (job.type === 'semantic-match') {
                    if (job.status === 'done') {
                        currentChain.matchDone = true;
                        updateFile(fileId, { matchStatus: 'done' });
                        console.log(`[Train] Match done (file=${fileId.slice(0,8)})`);
                    } else if (job.status === 'error') {
                        updateFile(fileId, {
                            matchStatus: 'error',
                            error: job.error_message || 'Semantic match thất bại',
                        });
                        setError(job.error_message || 'Semantic match thất bại');
                        setIsMatching(false);
                    }
                }

                if (job.type === 'translate') {
                    if (job.status === 'done') {
                        currentChain.translatedBatches += 1;
                        const progress = {
                            completedBatches: currentChain.translatedBatches,
                            totalBatches: currentChain.totalBatches,
                            completedSegments: 0,  // không biết per-batch segment count từ đây
                            totalSegments: 0,
                        };
                        updateFile(fileId, { translateProgress: progress });

                        if (currentChain.translatedBatches >= currentChain.totalBatches) {
                            // Tất cả batches translate xong → set translationStatus='done'
                            // aiOutput sẽ được merge qua useBatchRecovery khi pending_sync đến
                            updateFile(fileId, { translationStatus: 'done' });
                            setIsTranslating(false);
                            console.log(`[Train] All ${currentChain.totalBatches} translate batches done`);
                        }
                    } else if (job.status === 'error') {
                        // Ghi nhận batch lỗi nhưng không kết thúc sớm — finalizer server xử lý retry
                        console.warn(`[Train] Translate batch error (job=${job.id?.slice(0,8)}): ${job.error_message}`);
                    }
                }
            })
            .subscribe();

        // Channel 2: lắng nghe audit job trực tiếp (WHERE id=auditId) cho audit done/error
        const auditChannel = supabase
            .channel(`train-audit-${auditId}`)
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'public',
                table: 'jobs',
                filter: `id=eq.${auditId}`,
            }, (payload) => {
                const job = payload.new as any;
                const currentChain = activeChainRef.current;
                if (!currentChain || currentChain.auditId !== auditId) return;

                if (job.status === 'pending' || job.status === 'queued' || job.status === 'running') {
                    // Audit đang được dispatch → cập nhật UI
                    updateFile(fileId, { auditStatus: 'running', currentStep: 4 });
                    setIsAuditing(true);
                    console.log(`[Train] Audit dispatched (status=${job.status})`);
                } else if (job.status === 'done') {
                    // Audit xong → auditResult sẽ đến qua pending_sync + useBatchRecovery
                    // Tuy nhiên ta cần cập nhật auditStatus ngay để UI phản hồi nhanh
                    updateFile(fileId, { auditStatus: 'done', currentStep: 4 });
                    setIsAuditing(false);
                    console.log(`[Train] Audit done (file=${fileId.slice(0,8)})`);
                } else if (job.status === 'error') {
                    updateFile(fileId, {
                        auditStatus: 'error',
                        error: job.error_message || 'Audit thất bại',
                    });
                    setIsAuditing(false);
                    setError(job.error_message || 'Audit thất bại');
                    console.warn(`[Train] Audit error: ${job.error_message}`);
                }
            })
            .subscribe();

        // Pending_sync content delivery (audit result, translate aiOutput, match status)
        // → Handled by useBatchRecovery (10s poll, unified for cả 3 type).
        // Train workflow chỉ giữ Channel 1 + 2 cho status updates Realtime trên jobs table.

        return () => {
            supabase.removeChannel(upstreamChannel);
            supabase.removeChannel(auditChannel);
        };
    }, [auditSubscribeId, updateFile]);

    // ─── runFileAuto ───────────────────────────────────────────────────────
    /**
     * REFACTORED: thay thế sequential runJobAndWait × 3 bằng 1 RPC call `create_train_chain`.
     * Server INSERT atomic: 1 match + N translate + 1 audit (status='waiting').
     * Server workers tự chạy M → T(parallel) → A theo chain.
     *
     * Client chỉ cần:
     *  1. Build input data (segments, config, batches)
     *  2. Gọi RPC create_train_chain → nhận (match_id, translate_run_id, audit_id)
     *  3. Subscribe jobs table để cập nhật UI progress
     *  4. useBatchRecovery (existing) tự merge kết quả khi đủ pending_sync
     */
    const runFileAuto = async (fileId: string) => {
        // Ensure file content loaded trước khi đọc sourceText
        const { ensureFileContentLoaded } = await import('../../../hooks/useFileSystem');
        await ensureFileContentLoaded(fileId);

        const freshFiles = useProjectStore.getState().files;
        const freshProjects = useProjectStore.getState().projects;
        const file = freshFiles.find(f => f.id === fileId);
        const project = freshProjects.find(p => p.id === file?.projectId);
        if (!file || !project) return;
        if (!user) {
            setError('Chưa đăng nhập');
            return;
        }

        setError(null);

        const projectConfig = {
            ...config,
            systemInstruction: project.systemPrompt || config.systemInstruction,
            extraRules: synonymGroupsToFlatText(
                project.type === 'translate'
                    ? (project.importedTerminology || [])
                    : (project.synonymGroups || [])
            ) || config.extraRules,
            synonymGroups: project.type === 'translate'
                ? (project.importedTerminology || [])
                : (project.synonymGroups || []),
            protectedPatterns: config.protectedPatterns || [],
            locale: project.locale || config.locale,
            sourceLang: project.sourceLang || 'vi',
            targetLang: project.targetLang || 'en',
            auditOutputLang: project.auditOutputLang || 'vi',
        };

        const sourceSegments = getTrainSourceSegments(file);
        const humanBenchmark = file.humanBenchmark || project.humanBenchmark || '';
        const humanSegments = getLineSegments(humanBenchmark).map(s => s.text);

        // Build matchedRules (giữ logic cũ từ translate workflow)
        const synonymGroups = project.type === 'translate'
            ? (project.importedTerminology || [])
            : (project.synonymGroups || []);
        const matchedGroups = findMatchingSynonymGroupsForSegments(file.sourceText || '', synonymGroups);
        const matchedRulesText = matchedGroups
            .map(m => {
                const defaultTarget = m.group.targets.find((t: any) => t.isDefault)?.text || m.group.targets[0]?.text || '';
                return `"${m.match}" -> "${defaultTarget}"`;
            })
            .join('\n');

        // Chunk segments (lazy load batchChunker)
        const { chunkSegments } = await import('../../../utils/batchChunker');
        const batches = chunkSegments(sourceSegments);
        const totalBatches = batches.length;
        const translateRunId = crypto.randomUUID();

        addDebugLog(
            'Train Chain Create (Phase 4.3)',
            projectConfig.systemInstruction,
            `${sourceSegments.length} segments → ${totalBatches} translate batches`,
            undefined,
            fileId
        );

        // Khởi tạo UI state: tất cả pending → running (match + translate cùng lúc, server song song)
        setIsMatching(true);
        setIsTranslating(true);
        updateFile(fileId, {
            matchStatus: 'running',
            translationStatus: 'running',
            auditStatus: 'pending',
            currentStep: 2.5,
            aiOutput: [],
            semanticMatchResult: undefined,
            auditResult: undefined,
            auditRawOutput: undefined,
            auditUsage: undefined,
            error: undefined,
            translateProgress: {
                completedBatches: 0,
                totalBatches,
                completedSegments: 0,
                totalSegments: sourceSegments.length,
            },
        });

        try {
            // ─── STEP 1: Insert chain jobs via /api/jobs Worker gate ────────────
            // Replaces supabase.rpc('create_train_chain') which was blocked by RLS.
            // Worker gate: JWT verify + server-side cost compute + charge atomic.
            //
            // Order matters for FK constraints:
            //   A) Insert audit FIRST (status='waiting') — FK target for match + translate
            //   B) Insert match (update_to=auditId)
            //   C) Insert translate batches in sequence (update_to=auditId, shared translateRunId)
            //
            // Failure: if any insertJob throws → no partial chain (audit already inserted but
            // Worker finalizer will see it never gets M+T deps and it stays 'waiting' forever).
            // User can retry runFileAuto — new auditId will be created; stale 'waiting' job will
            // be cleaned up by existing watchdog (or admin). Acceptable for train flow (non-billing-critical).

            const { insertJob: workerInsertJob, insertJobs: workerInsertJobs } = await import('../../../services/api/workerClient');

            // Bật dispatch flag → beforeunload warning user nếu refresh giữa chừng API window.
            const { useDispatchStore } = await import('../../../store/useDispatchStore');
            useDispatchStore.getState().begin();
            let insertedAuditId: string;
            try {
                // A) Insert audit job (status='waiting', billing via train_pre_translate_audit factor)
                // insertedAuditId = server-assigned job_id (used as FK for update_to in M+T jobs)
                insertedAuditId = await workerInsertJob({
                    type: 'audit',
                    fileId,
                    fileName: file.fileName,
                    runId: crypto.randomUUID(),  // audit is a single job; run_id = job_id effectively
                    privacyMode: 'cloud_sync',
                    billingContext: 'train_pre_translate_audit',
                    initialStatus: 'waiting',
                    input: {
                        sourceSegments,
                        // humanSegments=null → try_finalize_audit uses NULL check to gate on match done
                        // aiSegments={} → keyed object; slots filled by update_audit_ai_slot per translate batch
                        humanSegments: null,
                        aiSegments: {},
                        config: projectConfig,
                        matchedRules: matchedRulesText,
                        // Hint for finalizer: how many translate batches to wait for
                        translate_run_id: translateRunId,
                        translate_total_batches: batches.length,
                    },
                });

                // B) Insert semantic-match job (update_to=auditId → chainNotifyMatchDone writes humanSegments)
                await workerInsertJob({
                    type: 'semantic-match',
                    fileId,
                    fileName: file.fileName,
                    runId: crypto.randomUUID(),
                    privacyMode: 'cloud_sync',
                    updateTo: insertedAuditId,
                    input: {
                        viSegments: sourceSegments,
                        enSegments: humanSegments,
                        // IP protection: KHÔNG gửi model — Worker hardcode server-side
                        config: {},
                    },
                });

                // C) Insert translate batches (shared translateRunId, update_to=auditId)
                await workerInsertJobs(batches.map((b, i) => ({
                    type: 'translate' as const,
                    fileId,
                    fileName: file.fileName,
                    batchIndex: i,
                    totalBatches: batches.length,
                    runId: translateRunId,
                    privacyMode: 'cloud_sync' as const,
                    updateTo: insertedAuditId,
                    input: {
                        segments: b.segments,
                        config: projectConfig,
                    },
                })));

                // Persist disk TRONG begin/end window — beforeunload prompt vẫn active
                // cho đến khi disk có status='running'. Tránh race window 50-200ms sau API done.
                try {
                    const { saveFileImmediately: _save } = await import('../../../services/shared/saveFileImmediately');
                    const { useFileSystemStore: _fsStore } = await import('../../../store/useFileSystemStore');
                    const _fsState = _fsStore.getState();
                    if (_fsState.isConnected && _fsState.rootHandle) {
                        const _updated = useProjectStore.getState().files.find(f => f.id === fileId);
                        if (_updated) {
                            await _save(_fsState.rootHandle, project, _updated);
                        }
                    }
                } catch (e) {
                    console.warn('[Train] saveFileImmediately failed (non-blocking):', e);
                }
            } finally {
                useDispatchStore.getState().end();
            }

            const actualTranslateRunId: string = translateRunId;

            console.log(
                `[Train] Chain created: TR=${actualTranslateRunId.slice(0, 8)} A=${insertedAuditId.slice(0, 8)} (${totalBatches} translate batches)`
            );

            // ─── STEP 2: Ghi active chain vào ref + kích hoạt subscription ─────
            activeChainRef.current = {
                fileId,
                auditId: insertedAuditId,
                translateRunId: actualTranslateRunId,
                totalBatches,
                translatedBatches: 0,
                matchDone: false,
            };
            // Trigger useEffect subscription bằng cách update state auditSubscribeId
            setAuditSubscribeId(insertedAuditId);

            // ─── STEP 3: Return ngay — server tự lo chain ─────────────────────
            // KHÔNG await như cũ. UI sẽ cập nhật qua Realtime subscription.
            // useBatchRecovery (existing) sẽ merge kết quả khi đủ pending_sync rows.
            console.log(`[Train] Chain dispatched — server orchestrating. Subscribed to audit=${insertedAuditId.slice(0,8)}`);

        } catch (e: any) {
            console.error('[Train] runFileAuto failed:', e);
            updateFile(fileId, {
                matchStatus: 'error',
                translationStatus: 'error',
                auditStatus: 'error',
                error: e.message,
            });
            setError(e.message);
            setIsMatching(false);
            setIsTranslating(false);
            setIsAuditing(false);
            activeChainRef.current = null;
        }
        // KHÔNG có finally setIsTraining(false) ở đây — isMatching/isTranslating/isAuditing
        // được clear bởi subscription handlers khi từng phase done.
    };

    /**
     * Run train workflow for all pending files in a project.
     */
    const runAllTrain = useCallback(async (projectId: string) => {
        const project = projects.find(p => p.id === projectId);
        const trainFiles = files.filter(f =>
            f.projectId === projectId &&
            (f.humanBenchmark || project?.humanBenchmark) &&
            (f.translationStatus === 'pending' || f.translationStatus === 'error')
        );

        for (const file of trainFiles) {
            await runFileAuto(file.id);
        }
    }, [files, projects, runFileAuto]);

    /**
     * Run ONLY the audit step for a single file (using existing translate + match results).
     * Giữ nguyên code cũ — dùng khi chạy audit riêng sau khi đã có translate result.
     */
    const runAuditStep = async (fileId: string) => {
        const file = files.find(f => f.id === fileId);
        const project = projects.find(p => p.id === file?.projectId);
        if (!file || !project) return;

        const aiOutput = file.aiOutput || [];
        if (aiOutput.length === 0) {
            updateFile(fileId, { auditStatus: 'error', error: 'Chua co ket qua dich. Hay chay translate truoc.' });
            return;
        }
        if (!file.semanticMatchResult) {
            updateFile(fileId, { auditStatus: 'error', error: 'Chua co ket qua match. Hay chay semantic match truoc.' });
            return;
        }

        setError(null);
        setIsAuditing(true);

        const projectConfig = {
            ...config,
            systemInstruction: project.systemPrompt || config.systemInstruction,
            extraRules: synonymGroupsToFlatText(project.synonymGroups || []) || config.extraRules,
            synonymGroups: project.synonymGroups || [],
            protectedPatterns: config.protectedPatterns || [],
            locale: project.locale || config.locale,
            sourceLang: project.sourceLang || 'vi',
            targetLang: project.targetLang || 'en',
            auditOutputLang: project.auditOutputLang || 'vi',
        };

        const auditJobId = addJob('audit', fileId, file.fileName);
        updateFile(fileId, { auditStatus: 'running', auditResult: undefined, currentStep: 4, error: undefined });
        setAuditStreamingText('');
        setAuditThinkingText('');

        try {
            const sourceSegments = getTrainSourceSegments(file);
            const humanBenchmark = file.humanBenchmark || project.humanBenchmark || '';
            const humanSegments = getLineSegments(humanBenchmark).map(s => s.text);

            let humanForAudit = humanSegments;
            if (file.semanticMatchResult?.mappings) {
                humanForAudit = file.semanticMatchResult.mappings.map(m =>
                    m.isUnmatched ? 'MISSING' : m.humanText
                );
            }

            const { runJobAndWait } = await import('../../../services/api/workerClient');

            addDebugLog('Audit (Standalone - Jobs)', projectConfig.systemInstruction, `${sourceSegments.length} segments`, undefined, fileId);

            const { result: auditResponse, usage: auditUsageRaw } = await runJobAndWait({
                type: 'audit',
                fileId,
                fileName: file.fileName,
                billingContext: 'audit_bundle',
                input: {
                    sourceSegments,
                    aiSegments: aiOutput,
                    humanSegments: humanForAudit,
                    config: projectConfig,
                    matchedRules: file.matchedRules,
                    semanticMatchMappings: file.semanticMatchResult?.mappings || undefined,
                },
            });

            const auditUsage = { ...auditUsageRaw, totalTokens: auditUsageRaw.inputTokens + auditUsageRaw.outputTokens };
            completeJob(auditJobId, auditUsage);

            const finalAuditResult = auditResponse;
            if (file.semanticMatchResult?.mappings && finalAuditResult.segmentMappings) {
                finalAuditResult.segmentMappings.forEach((am: any) => {
                    const srcIdx = (am.sourceIndex || 0) - 1;
                    const sm = file.semanticMatchResult!.mappings.find(m => m.sourceIndex === srcIdx);
                    if (sm) {
                        am.humanIndices = sm.allHumanIndices || [sm.humanIndex];
                        am.humanIndicesRaw = sm.allHumanIndices?.join(', ') || `${sm.humanIndex}`;
                        am.isMissing = sm.isUnmatched;
                    }
                });
            }

            updateFile(fileId, {
                auditResult: finalAuditResult,
                auditScore: finalAuditResult?.overallScore,
                auditStatus: 'done',
                auditUsage,
                currentStep: 4,
                error: undefined,
            });
        } catch (e: any) {
            console.error('runAuditStep failed:', e);
            failJob(auditJobId, e.message);
            updateFile(fileId, { auditStatus: 'error', error: e.message });
            setError(e.message);
        } finally {
            setIsAuditing(false);
        }
    };

    return {
        runFileAuto,
        runAllTrain,
        runAuditStep,
        isTranslating,
        isMatching,
        isAuditing,
        error,
        auditStreamingText,
        auditThinkingText,
    };
};
